import {CSSProperties, useEffect, useState} from 'react'
import _ from 'lodash'
import {SVGPie} from '@/features/plots'
import {IEvent, IUserLabelsMap} from '../../../labeling/contexts/labeling'
import {hexToRGB, hslToHex} from '@/utils/colors'
import './style.css'

export interface IUpNextConfig {
  types?: string[]
  includeLabels?: boolean
}

export interface IUpNextProps {
  data?: IEvent[]
  userLabels?: IUserLabelsMap
  currentTime?: number
  config?: IUpNextConfig
  onValueClick?: (value: IEvent) => void
}

const defaultConfig: IUpNextConfig = {
  includeLabels: true,
}

export function UpNext(props: IUpNextProps) {
  const {data, userLabels, currentTime, config, onValueClick} = props

  const bucketWindow = 60
  const noticeWindow = 5
  const numNeighborBucketsToReadBefore = 5
  const numNeighborBucketsToReadAfter = 1
  const flashWindow = 0.2

  const {includeLabels, types} = config || defaultConfig

  const [buckets, setBuckets] = useState<{[key: string]: IEvent[]}>()
  const [currentBucket, setCurrentBucket] = useState<number>()
  const [pendingEvents, setPendingEvents] = useState<IEvent[]>()

  useEffect(() => {
    const labels =
      includeLabels ?
        _.chain(userLabels)
          .values()
          .flatMap(_.values)
          .map((v) => ({
            ...v,
            text: v.score !== undefined ? v.score : undefined,
          }))
          .value()
      : []
    const res = _.chain(data)
      .concat(labels)
      .filter((event) => {
        if (!types) return true
        return _.includes(types, event.type)
      })
      .groupBy((p) => timeToBucket(p.start))
      .value()
    setBuckets(res)
  }, [data, userLabels, includeLabels, types])

  useEffect(() => {
    let t: number | undefined
    if (currentTime) t = timeToBucket(currentTime)
    if (t === currentBucket) return
    setCurrentBucket(t)
  }, [currentTime])

  useEffect(() => {
    let e: IEvent[] | undefined
    if (currentBucket !== undefined && buckets) {
      e = _.chain(buckets)
        .pick(
          _.range(
            currentBucket - numNeighborBucketsToReadBefore,
            currentBucket + numNeighborBucketsToReadAfter + 1
          )
        )
        .values()
        .flatten()
        .value()
    }
    setPendingEvents(e)
  }, [currentBucket, buckets])

  function handleOnValueClick(value: IEvent) {
    if (!onValueClick) return
    onValueClick(value)
  }

  function timeToBucket(time: number) {
    return _.floor(time / bucketWindow)
  }

  function renderNotices(): React.ReactNode {
    if (!(pendingEvents && pendingEvents.length > 0)) return null
    if (currentTime === undefined) return null
    return (
      _.chain(pendingEvents)
        .filter((e) => {
          const t = currentTime - e.start
          if (e.end !== undefined) {
            if (currentTime > e.end) return false
          } else {
            if (currentTime > e.start + flashWindow * 2) return false
          }
          if (t < -noticeWindow) return false
          return true
        })
        // .uniqBy(e => e.type)
        .orderBy((e) => currentTime - e.start, 'desc')
        .map((e) => {
          const t = currentTime - e.start
          const w = t / noticeWindow
          const p = e.end !== undefined ? t / (e.end - e.start) : 1
          let color: CSSProperties['backgroundColor']
          if (_.isNumber(e.hue)) {
            // color = hslToHex(e.hue);
            color = `rgba(${hexToRGB(hslToHex(e.hue)).join(', ')}, 0.5`
          }
          // if (t > 0) return null;
          return (
            <div
              key={`${e.type}/${e.start}-${e.end}`}
              className='notice'
              onClick={() => handleOnValueClick(e)}
              style={{
                ...(color && t >= 0 ?
                  {
                    backgroundColor: color,
                  }
                : undefined),
                ...(t >= 0 && t <= flashWindow ?
                  {
                    borderColor: 'white',
                  }
                : undefined),
              }}
            >
              {e.text ? `${e.type}: ${e.text}` : e.type}
              {/* ({e.start}{e.end !== undefined ? ` - ${e.end}` : null}) */}
              <div className='pie'>
                <SVGPie
                  radius={20}
                  data={
                    t < 0 ?
                      [
                        {value: 1 - Math.abs(w), color: 'transparent'},
                        {value: Math.abs(w), color: 'rgba(255, 255, 255, 0.5)'},
                      ]
                    : [
                        {value: p, color: 'white'},
                        {value: 1 - p, color: 'transparent'},
                      ]
                  }
                />
                {t < 0 ?
                  <div className='value'>{Math.ceil(Math.abs(t))}</div>
                : null}
              </div>
            </div>
          )
        })
        .uniqBy('key')
        .value()
    )
  }

  return <div className='up-next'>{renderNotices()}</div>
}
